import { useFormContext } from 'react-hook-form';
import type { CampaignEntry } from '../api';
import { useCampaigns } from '../api';
import { useCallback, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { Checkbox, ChipInput, CircularProgress, SuspenseErrorFallback } from 'components/common';
import { FormContainer } from 'components/layout';
import { CampaignStatusSwitcher, DuplicateStatusMenu } from './FacebookFormComponents';
import { CAMPAIGN_NAME_FILTER } from '../constants';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { isEmpty } from 'lodash-es';
import { isAppError } from 'lib/api';
import type { FacebookFormData } from '../validation/facebookValidator';
import { MemoizedCampaignBudgetField } from './CampaignBudgetField';
import { BudgetsHeader } from './TableHeaders';
import { differenceInHours, format, toDate } from 'date-fns';
import CachedIcon from '@mui/icons-material/Cached';
import { getVisibleCacheColor } from 'utils/getVisibleCacheColor';

const CAMPAIGNS_LIMIT = 20;

const gridStyles = () => ({
  '.MuiDataGrid-row .MuiInputAdornment-positionEnd': {
    visibility: 'hidden',
  },
  '.MuiDataGrid-row:hover .MuiInputAdornment-positionEnd': {
    visibility: 'visible',
  },
  '& .MuiDataGrid-main': {
    overflow: 'hidden',
  },
});

const createColumns = (
  isMarketingProcessInbox: boolean
): GridColDef<CampaignEntry>[] => [
  {
    field: 'id',
    headerName: 'Campaign ID',
    width: 160,
  },
  {
    field: 'name',
    headerName: 'Campaign Name',
    width: 750,
  },
  {
    field: 'budget',
    headerName: 'CBO Budget',
    disableColumnMenu: true,
    width: 240,
    renderCell: (params: GridRenderCellParams<CampaignEntry>) => (
      <MemoizedCampaignBudgetField cellParams={params} />
    ),
    renderHeader: (params: GridColumnHeaderParams<CampaignEntry>) => {
      return (
        <BudgetsHeader
          tooltip="CBO budget is calculated relative to the campaign"
          headerName={params.colDef.headerName}
        />
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 180,
    sortable: false,
    type: 'singleSelect',
    valueOptions: ['ACTIVE', 'PAUSED'],
    renderHeader: (params: GridColumnHeaderParams<CampaignEntry>) => {
      if (!isMarketingProcessInbox) {
        return <DuplicateStatusMenu formField="selectedCampaigns" />;
      }
      return params.colDef.headerName;
    },
    renderCell: (
      params: GridRenderCellParams<CampaignEntry, CampaignEntry['status']>
    ) => {
      if (!isMarketingProcessInbox) {
        return <CampaignStatusSwitcher cellParams={params} />;
      }
      return params.row.status;
    },
  },
];

const useCampaignSelection = () => {
  const { watch, setValue } = useFormContext();

  const selectedCampaignsRows = watch('selectedCampaigns') as CampaignEntry[];

  const selectedCampaigns = useMemo(
    () => selectedCampaignsRows?.map((c) => c.id) ?? [],
    [selectedCampaignsRows],
  );

  const updateSelectedCampaigns = useCallback(
    (campaigns: CampaignEntry[]) => {
      setValue('selectedCampaigns', campaigns, { shouldValidate: true });
    },
    [setValue],
  );

  return {
    selectedCampaigns,
    selectedCampaignsRows,
    updateSelectedCampaigns,
  };
};

const useWatchedFormValues = () => {
  const { watch } = useFormContext<FacebookFormData>();
  const filterCampaignsByName = watch('filterCampaignsByName');
  const selectedCampaignsOnly = watch('selectedCampaignsOnly');

  return {
    filterByName: filterCampaignsByName,
    selectedCampaignsOnly,
  };
};

const LoadingOverlay = ({ title }: { title: string }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    }}
  >
    <CircularProgress title={title} />
  </Box>
);

const DataGridFooter = ({ selectedCount }: { selectedCount: number }) => {
  if (!selectedCount) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      {selectedCount === CAMPAIGNS_LIMIT ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            {`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {selectedCount} rows selected
        </Typography>
      )}
    </Box>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Typography
    sx={(theme) => ({
      color: theme.palette.grey[400],
      fontSize: theme.typography.h4.fontSize,
    })}
  >
    {message}
  </Typography>
);

const CampaignsGrid = () => {
  const { getValues } = useFormContext<FacebookFormData>();
  const { marketingProcess } = getValues();
  const { filterByName, selectedCampaignsOnly } = useWatchedFormValues();
  const { selectedCampaigns, selectedCampaignsRows, updateSelectedCampaigns } = useCampaignSelection();
  const params = useCampaignParams();

  const isMarketingProcessInbox = useMemo(
    () => CAMPAIGN_NAME_FILTER[marketingProcess]?.includes('inbox'),
    [marketingProcess],
  );

  const columns = useMemo(
    () => createColumns(isMarketingProcessInbox),
    [isMarketingProcessInbox],
  );

  const {
    data: rows,
    isPending,
    isError,
    error,
  } = useCampaigns({ params });

  const rowsMap = useMemo(
    () => new Map(rows?.map(row => [row.id, row]) ?? []),
    [rows],
  );

  const matchesFilter = useCallback(
    (name: string) =>
      !filterByName?.length ||
      filterByName.every(f => name.toLowerCase().includes(f.toLowerCase())),
    [filterByName],
  );

  const filteredRows = useMemo(() => {
    if (!rows?.length && !selectedCampaignsRows?.length) return [];

    const previousSelected =
      selectedCampaignsRows?.filter(c => !rowsMap.has(c.id)) ?? [];

    return [...previousSelected, ...(rows ?? [])].filter(
      row =>
        matchesFilter(row.name) &&
        (!selectedCampaignsOnly || selectedCampaigns.includes(row.id)),
    );
  }, [rows, rowsMap, selectedCampaignsRows, selectedCampaignsOnly, selectedCampaigns, matchesFilter]);

  const visibleSelectionModel = useMemo(() => {
    const selectedSet = new Set(selectedCampaigns);
    return filteredRows.filter(r => selectedSet.has(r.id)).map(r => r.id);
  }, [selectedCampaigns, filteredRows]);

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!rows) return;

      const parsedSelectedRows = new Set(
        selectedRows.slice(0, CAMPAIGNS_LIMIT).map(String),
      );

      const selectedRowsMap = new Map(
        selectedCampaignsRows?.map(row => [row.id, row]) ?? [],
      );

      // Preserve campaigns that are selected but not visible (hidden by filter or from another data set)
      const previousSelected = selectedCampaignsRows?.filter(
        c => !matchesFilter(c.name) || !rowsMap.has(c.id),
      ) ?? [];

      const currentSelected: CampaignEntry[] = [];
      for (const id of parsedSelectedRows) {
        const row = rowsMap.get(id);
        if (!row) continue;
        currentSelected.push({
          ...row,
          status: selectedRowsMap.get(id)?.status ?? row.status,
        });
      }

      updateSelectedCampaigns([...previousSelected, ...currentSelected]);
    },
    [rows, rowsMap, selectedCampaignsRows, matchesFilter, updateSelectedCampaigns],
  );

  if (!marketingProcess) {
    return <EmptyState message="Marketing process is required" />;
  }

  if (isError) {
    const errorMessage = isAppError(error)
      ? error.data?.message ?? error.message
      : 'An error occurred while fetching campaigns';

    return (
      <SuspenseErrorFallback
        title="Choose Campaigns"
        message={errorMessage}
      />
    );
  }

  if (isPending && isEmpty(filteredRows)) {
    return <CircularProgress title="Campaigns are loading..." />;
  }

  if (isEmpty(filteredRows)) {
    return <EmptyState message="No campaigns found" />;
  }

  return (
    <Box style={{ position: 'relative', height: 380, width: '100%' }}>
      {isPending && <LoadingOverlay title="Campaigns are loading..." />}

      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        rowSelectionModel={visibleSelectionModel}
        onRowSelectionModelChange={handleSelectionMode}
        hideFooterPagination
        disableColumnMenu
        getRowId={({ id }) => id}
        isRowSelectable={(params) =>
          selectedCampaigns.length < CAMPAIGNS_LIMIT ||
          selectedCampaigns.includes(params.row.id)
        }
        slots={{
          footer: () => <DataGridFooter selectedCount={selectedCampaigns.length} />,
        }}
        sx={gridStyles}
      />
    </Box>
  );
};

const useCampaignParams = () => {
  const { getValues } = useFormContext<FacebookFormData>();
  const { adAccounts: facebookAccountId, marketingProcess } = getValues();

  return useMemo(() => ({
    facebookAccountId,
    campaignNameFilter: CAMPAIGN_NAME_FILTER[marketingProcess] ?? '',
  }), [facebookAccountId, marketingProcess]);
};

const CampaignsForm = () => {
  const params = useCampaignParams();

  const { dataUpdatedAt, refetch, isFetching, isFetched } = useCampaigns({
    params,
  });

  const cacheColor = getVisibleCacheColor({ updatedAt: dataUpdatedAt });
  const isStale = differenceInHours(Date.now(), toDate(dataUpdatedAt)) > 4;

  return (
    <FormContainer
      title="Choose campaigns"
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 2,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Tooltip title="Campaigns might be stale, refresh if needed">
            <IconButton
              sx={{
                color: cacheColor,
                '&:hover': {
                  backgroundColor: `${cacheColor}15`,
                },
                animation: isStale ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
              onClick={() => {
                return refetch();
              }}
            >
              <CachedIcon
                sx={{
                  ...(isFetching && {
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }),
                }}
              />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle2">
            {isFetched && `updated at: ${format(toDate(dataUpdatedAt), 'dd/MM/yyyy HH:mm:ss')}`}
          </Typography>
        </Box>
      </Box>
      <CampaignsGrid />
    </FormContainer>
  );
};

export default CampaignsForm;